export type LegendItem = { type: "circle" | "line"; colour: string; label: string };

const PAD = 12;
const ROW_H = 22;
const ICON_W = 20;
const FONT = 13;
const RADIUS = 4;
const BG = "#000000";
const BG_OPACITY = "0.65";
const BORDER = "#ffffff";
const BORDER_OPACITY = "0.2";

function measureText(svg: SVGSVGElement, text: string, fontSize: number, bold = false): number {
  const t = document.createElementNS(svg.namespaceURI, "text") as SVGGraphicsElement;
  t.setAttribute("font-size", String(fontSize));
  t.setAttribute("font-family", "Arial, sans-serif");
  if (bold) t.setAttribute("font-weight", "bold");
  t.textContent = text;
  svg.appendChild(t);
  const w = t.getBBox().width;
  svg.removeChild(t);
  return w;
}

/**
 * Draws a legend card onto an SVG element positioned at bottom-left.
 * @param svg    - The SVG element (must be in the DOM for getBBox).
 * @param height - Viewport height in CSS pixels.
 * @param title  - Card title text.
 * @param sections - Each inner array is a group of items separated by a divider.
 *                   Empty sections are ignored.
 */
export function drawSvgLegend(
  svg: SVGSVGElement,
  height: number,
  title: string,
  sections: LegendItem[][],
): void {
  const nonEmpty = sections.filter(s => s.length > 0);
  if (nonEmpty.length === 0) return;

  const allItems = nonEmpty.flat();

  const maxLabelW = Math.max(...allItems.map(item => measureText(svg, item.label, FONT)));
  const titleW = measureText(svg, title, FONT + 1, true);
  const CARD_W = PAD + ICON_W + 4 + Math.max(maxLabelW, titleW) + PAD;

  const totalRows = allItems.length + (nonEmpty.length - 1) * 0.5;
  const cardH = PAD + FONT + PAD * 0.75 + totalRows * ROW_H + PAD;
  const cardX = PAD;
  const cardY = height - cardH - PAD;

  const card = document.createElementNS(svg.namespaceURI, "rect");
  card.setAttribute("x", String(cardX));
  card.setAttribute("y", String(cardY));
  card.setAttribute("width", String(CARD_W));
  card.setAttribute("height", String(cardH));
  card.setAttribute("rx", String(RADIUS));
  card.setAttribute("ry", String(RADIUS));
  card.setAttribute("fill", BG);
  card.setAttribute("fill-opacity", BG_OPACITY);
  card.setAttribute("stroke", BORDER);
  card.setAttribute("stroke-opacity", BORDER_OPACITY);
  card.setAttribute("stroke-width", "1");
  svg.appendChild(card);

  const titleEl = document.createElementNS(svg.namespaceURI, "text");
  titleEl.setAttribute("x", String(cardX + PAD));
  titleEl.setAttribute("y", String(cardY + PAD + FONT * 0.85));
  titleEl.setAttribute("font-size", String(FONT + 1));
  titleEl.setAttribute("font-family", "Arial, sans-serif");
  titleEl.setAttribute("font-weight", "bold");
  titleEl.setAttribute("fill", "#ffffff");
  titleEl.textContent = title;
  svg.appendChild(titleEl);

  let rowY = cardY + PAD + FONT + PAD * 0.75;

  const drawRow = ({type, colour, label}: LegendItem) => {
    const cy = rowY + ROW_H / 2;
    const iconCx = cardX + PAD + ICON_W / 2;

    if (type === "circle") {
      const c = document.createElementNS(svg.namespaceURI, "circle");
      c.setAttribute("cx", String(iconCx));
      c.setAttribute("cy", String(cy));
      c.setAttribute("r", "5");
      c.setAttribute("fill", colour);
      svg.appendChild(c);
    } else {
      const r = document.createElementNS(svg.namespaceURI, "rect");
      r.setAttribute("x", String(iconCx - 8));
      r.setAttribute("y", String(cy - 1));
      r.setAttribute("width", "16");
      r.setAttribute("height", "2");
      r.setAttribute("rx", "1");
      r.setAttribute("fill", colour);
      svg.appendChild(r);
    }

    const t = document.createElementNS(svg.namespaceURI, "text");
    t.setAttribute("x", String(cardX + PAD + ICON_W + 4));
    t.setAttribute("y", String(cy));
    t.setAttribute("font-size", String(FONT));
    t.setAttribute("font-family", "Arial, sans-serif");
    t.setAttribute("dominant-baseline", "central");
    t.setAttribute("fill", "#ffffff");
    t.textContent = label;
    svg.appendChild(t);

    rowY += ROW_H;
  };

  nonEmpty.forEach((section, i) => {
    section.forEach(drawRow);

    if (i < nonEmpty.length - 1) {
      const sep = document.createElementNS(svg.namespaceURI, "line");
      sep.setAttribute("x1", String(cardX + PAD));
      sep.setAttribute("y1", String(rowY + ROW_H * 0.25));
      sep.setAttribute("x2", String(cardX + CARD_W - PAD));
      sep.setAttribute("y2", String(rowY + ROW_H * 0.25));
      sep.setAttribute("stroke", BORDER);
      sep.setAttribute("stroke-opacity", BORDER_OPACITY);
      sep.setAttribute("stroke-width", "1");
      svg.appendChild(sep);
      rowY += ROW_H * 0.5;
    }
  });
}